import { analytics } from './analytics';
import { AnalyticsEvent } from './events/catalog';
import { resolveAnalyticsError } from './errorMessage';
import type { AnalyticsErrorContext } from './errorMessage';
import {
    durationSecSince,
    getAnalyticsVerificationMethod,
    getLastRegistrationStep,
    getRegistrationStartedAt,
    isRegistrationAbandonedTracked,
    isRegistrationCompleted,
    isRegistrationFailedTracked,
    markRegistrationAbandonedTracked,
    markRegistrationFailedTracked,
} from './registrationMeta';
import type { VerificationMethod } from './registrationMeta';
import type { RegistrationStep } from './screenName';

export type AnalyticsErrorFields = {
    error_code: string;
    error_message: string;
};

type ResolveErrorInput = {
    error?: unknown;
    errorCode?: string;
    fallback?: string;
    context?: AnalyticsErrorContext;
};

type ResolvedErrorInput = AnalyticsErrorFields;

function toErrorFields(
    options: ResolveErrorInput & Partial<ResolvedErrorInput>
): AnalyticsErrorFields {
    if (options.error_code != null && options.error_message != null) {
        return {
            error_code: options.error_code,
            error_message: options.error_message,
        };
    }
    return resolveAnalyticsError({
        error: options.error,
        errorCode: options.errorCode,
        fallback: options.fallback,
        context: options.context,
    });
}

/**
 * Fires `registration_failed` at most once per registration session.
 * Pass pre-resolved `error_code` / `error_message` when a sibling event
 * (e.g. `otp_failed`) already resolved them — avoids double resolve.
 * Always returns the error fields (even when the event is skipped).
 */
export function trackRegistrationFailed(
    options: {
        step: RegistrationStep;
    } & ResolveErrorInput &
        Partial<ResolvedErrorInput>
): AnalyticsErrorFields {
    const resolved = toErrorFields(options);

    if (isRegistrationFailedTracked() || isRegistrationCompleted()) {
        return resolved;
    }

    // Mark only after delivery — fail open so a dropped event can retry.
    const sent = analytics.track(AnalyticsEvent.REGISTRATION_FAILED, {
        step: options.step,
        verification_method: getAnalyticsVerificationMethod(),
        ...resolved,
    });
    if (sent) {
        markRegistrationFailedTracked();
    }

    return resolved;
}

/**
 * Resolves error fields once, fires `biometric_verification_failed`,
 * and optionally terminal `registration_failed` with the same fields.
 */
export function trackBiometricVerificationFailed(
    options: {
        verification_method: VerificationMethod;
        /** When set, also fires deduped `registration_failed`. */
        registrationFailedStep?: RegistrationStep;
    } & ResolveErrorInput &
        Partial<ResolvedErrorInput>
): AnalyticsErrorFields {
    const resolved = toErrorFields(options);

    analytics.track(AnalyticsEvent.BIOMETRIC_VERIFICATION_FAILED, {
        verification_method: options.verification_method,
        ...resolved,
    });

    if (options.registrationFailedStep) {
        trackRegistrationFailed({
            step: options.registrationFailedStep,
            ...resolved,
        });
    }

    return resolved;
}

/**
 * Best-effort abandon on unload. Uses last funnel step stored during
 * navigation — router `location` is unreliable during `pagehide`.
 */
export function trackRegistrationAbandoned(): void {
    if (
        isRegistrationAbandonedTracked() ||
        isRegistrationCompleted() ||
        isRegistrationFailedTracked()
    ) {
        return;
    }

    const step = getLastRegistrationStep();
    if (!step) {
        return;
    }

    const sent = analytics.track(AnalyticsEvent.REGISTRATION_ABANDONED, {
        step,
        verification_method: getAnalyticsVerificationMethod(),
        duration_sec: durationSecSince(getRegistrationStartedAt()) ?? 0,
    });
    if (sent) {
        markRegistrationAbandonedTracked();
    }
}
